import type { AttendanceStatus, ChartEntry, TodayPunchData } from "./types.ts";
// import { STATUS_LABELS, WEEKEND_DAYS } from "./constants.ts";
import {
  CHART_WEEKEND_MIN_BAR_HOURS,
  CHART_Y_MAX,
  STATUS_LABELS,
  WFH_LEAVE_TYPE,
  LEAVE_TYPES,
  WEEKEND_DAYS,
  labelForLeaveType,
} from "./constants.ts";

// ─────────────────────────────────────────────────────────────────────────────

/** "03:04 PM" | "NA" | null | undefined  →  displayable string or null */
export const formatTime = (value?: string | null): string | null => {
  if (!value || value.trim() === "NA") return null;
  return value.trim();
};

/** New REGULAR arrival timing is effective from 1 November 2026 (inclusive). */
export const isRegularTimingEffective = (isoDate?: string | null): boolean =>
  typeof isoDate === "string"
  && /^\d{4}-\d{2}-\d{2}$/.test(isoDate)
  && isoDate >= "2026-11-01";

/** Display-only comparison for a past punch when only today's shift is configured. */
export const comparePunchToCurrentShift = (
  punchIn?: string | null,
  shiftStart?: string | null,
  graceEndsAt?: string | null,
): { minutesAfterStart: number; isAfterGrace: boolean } | null => {
  const clockMinutes = (value?: string | null): number | null => {
    const match = value?.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    return hours < 24 && minutes < 60 ? hours * 60 + minutes : null;
  };

  const arrival = clockMinutes(punchIn);
  const start = clockMinutes(shiftStart);
  const grace = clockMinutes(graceEndsAt);
  if (arrival == null || start == null || grace == null) return null;
  // A current shift crossing midnight cannot establish a historical late status.
  if (grace < start) return null;
  return {
    minutesAfterStart: Math.max(0, arrival - start),
    isAfterGrace: arrival > grace,
  };
};

/** "07:12" → 7.2 (decimal hours for recharts) */
export const parseHours = (totalHour?: string | null): number => {
  if (!totalHour || totalHour === "NA") return 0;
  const [h = 0, m = 0] = totalHour.split(":").map(Number);
  return h + m / 60;
};

const MONTH_MAP: Record<string, string> = {
  Jan: "01",
  Feb: "02",
  Mar: "03",
  Apr: "04",
  May: "05",
  Jun: "06",
  Jul: "07",
  Aug: "08",
  Sep: "09",
  Oct: "10",
  Nov: "11",
  Dec: "12",
};

export const dateStringToISO = (dateStr: string): string => {
  // "Wed, Apr 01" → ["Apr", "01"]
  const parts = dateStr.split(",")[1]?.trim().split(" ");
  if (!parts || parts.length < 2) return "";

  const month = MONTH_MAP[parts[0]];
  const day = parts[1]?.padStart(2, "0");
  const year = new Date().getFullYear();

  if (!month || !day) return "";
  return `${year}-${month}-${day}`; // "2026-04-01"
};

/** Parse `YYYY-MM-DD` as local calendar date (avoids UTC midnight shifting the day). */
function parseLocalDate(isoDate: string): Date | null {
  const parts = isoDate.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d);
}

export const deriveStatus = (entry: any): AttendanceStatus => {
  if (!entry || !entry.date) return "absent";

  const dateObj = parseLocalDate(entry.date) ?? new Date(entry.date);
  const shortDay = dateObj.toLocaleDateString("en-US", {
    weekday: "short",
  });

  if ((WEEKEND_DAYS as readonly string[]).includes(shortDay)) {
    return "weekend";
  }

  const leaveType = entry.leaveType;
  if (leaveType == null || String(leaveType).trim() === "") {
    return "absent";
  }

  if (leaveType === WFH_LEAVE_TYPE) return "wfh";

  if ((LEAVE_TYPES as readonly string[]).includes(leaveType)) {
    return "leave";
  }

  if (leaveType === "Absent") return "absent";

  if (leaveType === "Present") return "present";

  return entry.inTime ? "present" : "absent";
};

export const parseWorkingHours = (value?: string | null): number => {
  if (!value) return 0;

  const hours = Number(value.match(/(\d+)h/)?.[1] || 0);
  const minutes = Number(value.match(/(\d+)m/)?.[1] || 0);

  return hours + minutes / 60;
};

function hasPunchTime(value: unknown): boolean {
  if (value == null) return false;
  const s = String(value).trim();
  return s !== "" && s.toUpperCase() !== "NA";
}

export const buildChartData = (
  entries: TodayPunchData[],
  currentShift?: Pick<TodayPunchData, "officeStartTime" | "graceEndsAt">,
): ChartEntry[] => {
  const rows: ChartEntry[] = (entries || [])
    .filter((entry): entry is TodayPunchData & { date: string } => Boolean(entry?.date))
    .map((entry) => {
      const dateObj = parseLocalDate(entry.date) ?? new Date(entry.date);
      const shortDay = dateObj.toLocaleDateString("en-US", {
        weekday: "short",
      });
      const status = deriveStatus(entry);
      const parsedHours = parseWorkingHours(entry.workingHours);
      let hours = parsedHours;
      let tooltipHoursLabel: string | undefined;

      if (status === "wfh") {
        const punched = hasPunchTime(entry.inTime) || hasPunchTime(entry.outTime);
        if (!punched) {
          hours = CHART_Y_MAX;
        } else if (parsedHours === 0) {
          hours = CHART_Y_MAX;
          tooltipHoursLabel = "Work from home";
        }
      } else if (status === "weekend") {
        if (parsedHours === 0) {
          hours = CHART_WEEKEND_MIN_BAR_HOURS;
        }
      } else if (status === "leave") {
        const punched = hasPunchTime(entry.inTime) || hasPunchTime(entry.outTime);
        if (!punched && parsedHours === 0) {
          hours = CHART_Y_MAX;
          tooltipHoursLabel = labelForLeaveType(entry.leaveType);
        }
      }

      const leaveTypeStr =
        entry.leaveType != null ? String(entry.leaveType) : "";
      const statusLabel =
        status === "leave"
          ? labelForLeaveType(leaveTypeStr)
          : STATUS_LABELS[status];
      const timingEffective = isRegularTimingEffective(entry.date);
      const shiftComparison = timingEffective && status === "present" && entry.arrivalStatus == null
        && !entry.officeStartTime
        ? comparePunchToCurrentShift(
            entry.inTime,
            currentShift?.officeStartTime,
            currentShift?.graceEndsAt,
          )
        : null;
      const isLate = timingEffective && status === "present" && (
        entry.arrivalStatus === "AFTER_GRACE" || shiftComparison?.isAfterGrace === true
      );

      return {
        shortDay,
        date: entry.date,
        isoDate: entry.date,
        status,
        statusLabel: isLate
          ? `${statusLabel} · Late arrival${shiftComparison ? " (current shift)" : ""}`
          : statusLabel,
        hours,
        isLate,
        ...(tooltipHoursLabel != null ? { tooltipHoursLabel } : {}),
        officeIn: formatTimeToTwelveHour(entry.inTime ?? null),
        officeOut: formatTimeToTwelveHour(entry.outTime ?? null),
      };
    });

  rows.sort((a, b) => a.isoDate.localeCompare(b.isoDate));
  return rows;
};
/** login_minutes → "05:30" */
export const formatLoginMinutes = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/** minutes → "7h 26m" (same shape as API `workingHours` strings) */
export const formatMinutesAsHm = (totalMinutes: number): string => {
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return "0h 0m";
  const rounded = Math.round(totalMinutes);
  const h = Math.floor(rounded / 60);
  const m = rounded % 60;
  return `${h}h ${m}m`;
};

export const formatLateDuration = (totalMinutes: number): string => {
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return "0 min";
  const rounded = Math.round(totalMinutes);
  const hours = Math.floor(rounded / 60);
  const minutes = rounded % 60;
  if (hours === 0) return `${minutes} min`;
  return minutes === 0 ? `${hours} hr` : `${hours} hr ${minutes} min`;
};

/**
 * API `activeWorkingHours`: minutes for the day (same unit as legacy `formatMinutesAsHm`).
 * `null` / `undefined` → em dash; `0` → "0h 0m".
 */
export const formatActiveWorkingHoursDisplay = (
  value?: number | null,
): string => {
  if (value == null) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return formatMinutesAsHm(n);
};

export const formatFullDate = (isoDate?: string) => {
  if (!isoDate) return "";

  const date = parseLocalDate(isoDate) ?? new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-US", {
    weekday: "long", // Monday
    month: "long", // March
    day: "numeric", // 5
    year: "numeric",
  });
};

/**
 * Converts a 24-hour time string ("13:21" or "13:21:56") to 12-hour format
 * ("1:21 PM"). Pure arithmetic — no Date object, no locale/timezone dependency.
 */
export function formatTimeToTwelveHour(time?: string | null): string {
  if (!time) return "--:--";

  const parts = time.split(":").map(Number);
  const h = parts[0];
  const m = parts[1];

  if (!Number.isFinite(h) || !Number.isFinite(m)) return "--:--";

  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  const minuteStr = String(m).padStart(2, "0");

  return `${hour12}:${minuteStr} ${period}`;
}
